// Analytics utility functions for FeatureTagDetailModal
// This module contains all the calculation logic separated from the UI component

/**
 * Calculate task age in days
 * @param {string} createdAt - ISO date string
 * @returns {number} Age in days
 */
export const calculateTaskAge = (createdAt) => {
  return Math.ceil((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
};

/**
 * Calculate days until due date
 * @param {string|null|undefined} dueDate - ISO date string
 * @returns {number|null} Days until due (null if no due date)
 */
export const calculateDaysUntilDue = (dueDate) => {
  if (!dueDate) return null;
  return Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
};

/**
 * Calculate basic task statistics
 * @param {Array} tagTasks - Array of tasks for the feature
 * @returns {Object} Task statistics
 */
export const calculateTaskStats = (tagTasks) => {
  const totalTasks = tagTasks.length;
  const completedTasks = tagTasks.filter(task => task.status === 'completed').length;
  const inProgressTasks = tagTasks.filter(task => task.status === 'in-progress').length;
  const openTasks = tagTasks.filter(task => task.status === 'open').length;
  const releasedTasks = tagTasks.filter(task => task.status === 'released').length;
  const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return {
    totalTasks,
    completedTasks,
    inProgressTasks,
    openTasks,
    releasedTasks,
    completionPercentage
  };
};

/**
 * Calculate hours statistics
 * @param {Array} tagTasks - Array of tasks for the feature
 * @returns {Object} Hours statistics
 */
export const calculateHoursStats = (tagTasks) => {
  const totalHours = tagTasks.reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
  const averageHoursPerTask = tagTasks.length > 0 ? Math.round((totalHours / tagTasks.length) * 10) / 10 : 0;
  const completedHours = tagTasks
    .filter(task => task.status === 'completed')
    .reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);

  return {
    totalHours,
    averageHoursPerTask,
    completedHours
  };
};

/**
 * Assess risk level based on due date and task completion
 * @param {string|null|undefined} dueDate - ISO date string
 * @param {number} totalTasks - Total number of tasks
 * @param {number} completedTasks - Number of completed tasks
 * @returns {Object} Risk assessment
 */
export const assessRisk = (dueDate, totalTasks, completedTasks) => {
  let riskLevel = 'low';
  let riskMessage = 'On track';
  
  // If no due date, return low risk (no urgency)
  if (!dueDate) return { riskLevel, riskMessage };

  const daysRemaining = calculateDaysUntilDue(dueDate);
  
  // Additional null check (defensive programming)
  if (daysRemaining === null) return { riskLevel, riskMessage };
  
  const incompleteTasks = totalTasks - completedTasks;
  const daysPerTask = (incompleteTasks > 0 && daysRemaining > 0) ? daysRemaining / incompleteTasks : 0;
  
  if (daysRemaining < 0) {
    riskLevel = 'critical';
    riskMessage = 'Overdue';
  } else if (daysRemaining <= 1) {
    riskLevel = 'high';
    riskMessage = 'Due very soon';
  } else if (daysPerTask < 1 && incompleteTasks > 0) {
    riskLevel = 'high';
    riskMessage = 'High risk - insufficient time per task';
  } else if (daysPerTask < 2 && incompleteTasks > 0) {
    riskLevel = 'medium';
    riskMessage = 'Medium risk - tight timeline';
  }

  return { riskLevel, riskMessage };
};

/**
 * Detect stagnant tasks based on status, hours, and age
 * @param {Array} tagTasks - Array of tasks for the feature
 * @returns {Array} Array of stagnant tasks
 */
export const detectStagnantTasks = (tagTasks) => {
  return tagTasks.filter(task => {
    const taskAge = calculateTaskAge(task.created_at);
    const hoursSpent = parseFloat(task.hours) || 0;
    
    // Check if task is stagnant
    const isStagnantOpen = task.status === 'open' && 
                          hoursSpent === 0 && 
                          taskAge >= 7;
    
    const isStagnantInProgress = task.status === 'in-progress' && 
                                taskAge >= 10;
    
    // Recent task exception (only if created_at exists)
    const isRecentTask = taskAge <= 2;
    const isRecentInProgressWithHours = task.status === 'in-progress' && 
                                      hoursSpent >= 5;
    
    // Apply recent task exception
    if (isRecentTask && !isRecentInProgressWithHours) {
      return false; // Ignore recent tasks unless they meet the exception
    }
    
    return isStagnantOpen || isStagnantInProgress;
  });
};

/**
 * Detect blockers based on feature due date and task characteristics
 * @param {Array} tagTasks - Array of tasks for the feature
 * @param {string|null|undefined} dueDate - ISO date string
 * @returns {Array} Array of blocker tasks
 */
export const detectBlockers = (tagTasks, dueDate) => {
  // Edge case: If feature has 0 tasks, set Blocker = false
  if (tagTasks.length === 0) {
    return [];
  }

  // If no due date, use Rule 2 (non-urgent features logic)
  if (!dueDate) {
    const stagnantTasks = detectStagnantTasks(tagTasks);
    const stagnantRatio = stagnantTasks.length / tagTasks.length;
    return stagnantRatio >= 0.20 ? stagnantTasks : [];
  }

  const daysUntilDue = calculateDaysUntilDue(dueDate);
  
  // Rule 1: Urgent features (Due in ≤ 3 days, inclusive)
  if (daysUntilDue !== null && daysUntilDue <= 3) {
    // Blocker = true if the feature has any tasks where status != completed
    return tagTasks.filter(task => task.status !== 'completed');
  }
  
  // Rule 2: Non-urgent features (Due in > 3 days or no due date)
  const stagnantTasks = detectStagnantTasks(tagTasks);
  
  // Blocker = true if stagnant_tasks / total_tasks ≥ 0.20 (i.e., ≥20%)
  const stagnantRatio = stagnantTasks.length / tagTasks.length;
  return stagnantRatio >= 0.20 ? stagnantTasks : [];
};

/**
 * Calculate assignee workload distribution
 * @param {Array} tagTasks - Array of tasks for the feature
 * @param {Array} users - Array of all users
 * @returns {Array} Array of assignee workload objects
 */
export const calculateAssigneeWorkload = (tagTasks, users) => {
  const assigneeWorkload = {};
  
  tagTasks.forEach(task => {
    if (task.assigned_to) {
      // Handle both cases: assigned_to as object or as ID
      const assigneeId = task.assigned_to.id || task.assigned_to;
      const assignee = task.assigned_to.display_name ? task.assigned_to : users.find(u => u.id === assigneeId);
      
      if (assignee) {
        const assigneeKey = assignee.id;
        
        if (!assigneeWorkload[assigneeKey]) {
          assigneeWorkload[assigneeKey] = {
            user: assignee,
            total: 0,
            completed: 0,
            inprogress: 0,
            open: 0
          };
        }
        
        assigneeWorkload[assigneeKey].total++;
        
        // Map task status to workload property
        const statusKey = task.status.replace('-', '');
        if (assigneeWorkload[assigneeKey].hasOwnProperty(statusKey)) {
          assigneeWorkload[assigneeKey][statusKey]++;
        }
      }
    }
  });

  return Object.values(assigneeWorkload);
};

/**
 * Main analytics calculation function
 * @param {Object} tag - Feature tag object
 * @param {Array} tasks - Array of all tasks
 * @param {Array} users - Array of all users
 * @returns {Object} Complete analytics object (never null)
 */
export const calculateFeatureAnalytics = (tag, tasks, users) => {
  if (!tag) return null;

  const tagTasks = tasks ? tasks.filter(task => task.feature_tag?.id === tag.id) : [];

  // Calculate all statistics using helper functions (handle empty task list)
  const taskStats = calculateTaskStats(tagTasks);
  const hoursStats = calculateHoursStats(tagTasks);
  const riskAssessment = assessRisk(tag.due_date, taskStats.totalTasks, taskStats.completedTasks);
  const blockers = detectBlockers(tagTasks, tag.due_date);
  const assigneeWorkload = calculateAssigneeWorkload(tagTasks, users || []);

  return {
    ...taskStats,
    ...hoursStats,
    daysRemaining: calculateDaysUntilDue(tag.due_date),
    ...riskAssessment,
    blockers,
    assigneeWorkload
  };
};
